import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import ServiceModal from '../components/ServiceModal';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editService, setEditService] = useState(null);

  // Récupère services depuis l'API
  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/services');
      setServices(res.data.filter(s => s.status !== 'Archivé'));
      setLoading(false);
    } catch (err) {
      toast.error('Erreur lors du chargement des services');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Ouvre le modal en mode création
  const handleAddClick = () => {
    setEditService(null);
    setModalVisible(true);
  };

  // Ouvre le modal en mode édition
  const handleEditClick = (service) => {
    setEditService(service);
    setModalVisible(true);
  };

  // Ferme le modal
  const handleModalClose = () => {
    setModalVisible(false);
  };

  // Submit ajout ou modification
  const handleModalSubmit = async (data) => {
    try {
      if (editService) {
        // Update
        await axios.put(`/api/services/${editService.id}`, data);
        toast.success('Service modifié avec succès');
      } else {
        // Create
        await axios.post('/api/services', data);
        toast.success('Service ajouté avec succès');
      }
      setModalVisible(false);
      fetchServices();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement du service");
    }
  };

  // "Supprime" (archive) un service
  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment archiver ce service ?')) return;
    try {
      await axios.put(`/api/services/${id}`, { status: 'Archivé' });
      toast.success('Service archivé');
      fetchServices();
    } catch (err) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  // Gestion couleur par type_service
  const getBadgeColor = (type) => {
    switch (type) {
      case 'IT': return 'bg-blue-500';
      case 'Marketing': return 'bg-pink-500';
      case 'Support': return 'bg-green-500';
      case 'RH': return 'bg-yellow-500 text-black';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Liste des Services</h1>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <FiPlus /> Ajouter
        </button>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="w-full border rounded shadow-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Nom</th>
              <th className="p-3 text-left">Description</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 && (
              <tr>
                <td colSpan={4} className="p-4 text-center text-gray-500">
                  Aucun service disponible
                </td>
              </tr>
            )}
            {services.map(service => (
              <tr key={service.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{service.name}</td>
                <td className="p-3">{service.description}</td>
                <td className="p-3">
                  <span
                    className={`inline-block px-3 py-1 rounded text-white text-sm ${getBadgeColor(service.type_service)}`}
                  >
                    {service.type_service}
                  </span>
                </td>
                <td className="p-3 text-center flex justify-center gap-3">
                  <button
                    onClick={() => handleEditClick(service)}
                    className="text-blue-600 hover:text-blue-800"
                    aria-label={`Modifier ${service.name}`}
                  >
                    <FiEdit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="text-red-600 hover:text-red-800"
                    aria-label={`Archiver ${service.name}`}
                  >
                    <FiTrash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ServiceModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        service={editService}
      />
    </div>
  );
}
